import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { paths } from './generated/schema';

/** A configured Greasemap API client. */
export type GreasemapClient = Client<paths>;

/** Options for {@link createGreasemapClient}. */
export interface GreasemapClientOptions {
  /** Absolute base URL of the API, for example `https://api.greasemap.app`. */
  readonly baseUrl: string;

  /**
   * Supplies the bearer token for each request.
   *
   * Called per request rather than captured once, so a token refreshed mid-session is picked up
   * without rebuilding the client. Return `undefined` to send the request anonymously.
   */
  readonly getAccessToken?: () => Promise<string | undefined> | string | undefined;

  /**
   * Extra headers applied to every request.
   *
   * Used in development to carry the simulated identity headers the API's development
   * authentication scheme reads. Never populated in production builds.
   */
  readonly defaultHeaders?: Readonly<Record<string, string>>;

  /**
   * Fetch implementation to use.
   *
   * Defaults to the platform `fetch`. Overridable so tests can assert on outgoing requests, and so
   * React Native can supply a polyfill if one is ever needed.
   */
  readonly fetch?: typeof globalThis.fetch;
}

/**
 * Creates a typed client bound to a Greasemap API instance.
 *
 * @param options - Client configuration.
 * @returns A client whose paths, parameters and responses are checked against the OpenAPI contract.
 */
export function createGreasemapClient(options: GreasemapClientOptions): GreasemapClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      for (const [key, value] of Object.entries(options.defaultHeaders ?? {})) {
        request.headers.set(key, value);
      }

      const token = await options.getAccessToken?.();

      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      return request;
    },
  };

  client.use(authMiddleware);

  return client;
}
